/**
 * Car Show Mode helpers — hero systems, suggestions, session progress.
 * Session-only; never persist another visitor's expanded state.
 */

import { getCategoryColor } from './categories';
import {
  buildSystemSummaries,
  countAssemblies,
  explorerNodeId,
  findNodeById,
  type ExplorerSystemSummary,
} from './buildExplorerUtils';
import {
  buildModificationForest,
  countChildren,
  type HierarchyModLike,
  type HierarchyNode,
} from './modificationHierarchy';

/** Default inactivity before returning to system overview (ms). */
export const CAR_SHOW_IDLE_MS = 15_000;
export const CAR_SHOW_TOUR_STEP_MS = 7_000;
export const CAR_SHOW_CARD_BACK_MS = 3_000;

/** Keep the first two stops predictable for a display left on a stand. */
export function carShowTourCategories(mods: Array<{ category?: string | null }>): string[] {
  const present = new Set(mods.map((mod) => mod.category?.trim()).filter((name): name is string => Boolean(name)));
  const preferred = ['Engine', 'Drivetrain', 'Fuel System', 'Cooling', 'Turbo', 'Suspension', 'Brakes', 'Wheels/Tires', 'Wheels', 'Exterior', 'Interior', 'Electronics', 'Exhaust'];
  return [...preferred.filter((name) => present.delete(name)), ...Array.from(present).sort()];
}

const HERO_CATEGORY_BOOST: Record<string, number> = {
  Engine: 10,
  Turbo: 9,
  Drivetrain: 8,
  Exhaust: 6,
  Suspension: 6,
  Brakes: 5,
  Electronics: 5,
  Exterior: 4,
  Interior: 3,
  Cooling: 4,
  'Fuel System': 5,
  Wheels: 3,
  Miscellaneous: 0,
};

export interface CarShowSuggestion {
  id: string;
  label: string;
  kind: 'system' | 'assembly' | 'guide';
  category?: string;
  focusId?: string;
  guide?: 'detailed' | 'with_media' | 'newest';
}

export interface CarShowProgress {
  systemsTotal: number;
  systemsExplored: number;
  partsTotal: number;
  partsFocused: number;
  assembliesOpened: number;
}

export function scoreHeroSystem(summary: ExplorerSystemSummary): number {
  const boost = HERO_CATEGORY_BOOST[summary.category] ?? 1;
  return boost * 10 + summary.assemblyCount * 3 + summary.partCount + (summary.imageUrl ? 5 : 0);
}

/** Systems ordered for car-show entry (hero assemblies first). */
export function orderSystemsForCarShow(summaries: ExplorerSystemSummary[]): ExplorerSystemSummary[] {
  return [...summaries].sort((a, b) => scoreHeroSystem(b) - scoreHeroSystem(a));
}

export function buildCarShowSuggestions<T extends HierarchyModLike & { image_url?: string | null }>(
  mods: T[]
): CarShowSuggestion[] {
  const systems = orderSystemsForCarShow(buildSystemSummaries(mods));
  const forest = buildModificationForest(mods);
  const out: CarShowSuggestion[] = [];

  if (systems[0]) {
    out.push({
      id: `sys-${systems[0].category}`,
      label: `Start with ${systems[0].category}`,
      kind: 'system',
      category: systems[0].category,
    });
  }
  if (systems[1]) {
    out.push({
      id: `sys-${systems[1].category}`,
      label: `Explore ${systems[1].category}`,
      kind: 'system',
      category: systems[1].category,
    });
  }

  let best: HierarchyNode<T> | null = null;
  let bestScore = -1;
  const visit = (node: HierarchyNode<T>) => {
    const kids = countChildren(node);
    if (kids > bestScore) {
      bestScore = kids;
      best = node;
    }
    node.children.forEach(visit);
  };
  forest.forEach(visit);
  if (best && bestScore >= 2) {
    const node = best as HierarchyNode<T>;
    out.push({
      id: `asm-${explorerNodeId(node)}`,
      label: 'Most detailed assembly',
      kind: 'assembly',
      focusId: explorerNodeId(node),
      category: node.mod.category || undefined,
    });
  }

  const withPhoto = mods.find((m) => m.image_url);
  if (withPhoto) {
    out.push({
      id: 'guide-media',
      label: 'Parts with photos',
      kind: 'guide',
      guide: 'with_media',
    });
  }

  out.push({
    id: 'guide-newest',
    label: 'Newest additions',
    kind: 'guide',
    guide: 'newest',
  });

  return out.slice(0, 5);
}

export function computeCarShowProgress(
  systems: ExplorerSystemSummary[],
  exploredSystems: Set<string>,
  focusedParts: Set<string>,
  openedAssemblies: Set<string>,
  partsTotal: number
): CarShowProgress {
  return {
    systemsTotal: systems.length,
    systemsExplored: systems.filter((s) => exploredSystems.has(s.category)).length,
    partsTotal,
    partsFocused: focusedParts.size,
    assembliesOpened: openedAssemblies.size,
  };
}

export function relationshipContext<T extends HierarchyModLike>(
  forest: HierarchyNode<T>[],
  focusId: string
): {
  parent: HierarchyNode<T> | null;
  children: HierarchyNode<T>[];
  siblings: HierarchyNode<T>[];
  pathTitles: string[];
} {
  const node = findNodeById(forest, focusId);
  if (!node) {
    return { parent: null, children: [], siblings: [], pathTitles: [] };
  }

  const findParentOf = (
    nodes: HierarchyNode<T>[],
    p: HierarchyNode<T> | null
  ): HierarchyNode<T> | null | undefined => {
    for (const n of nodes) {
      if (explorerNodeId(n) === focusId) return p;
      const found = findParentOf(n.children, n);
      if (found !== undefined) return found;
    }
    return undefined;
  };
  const parent = findParentOf(forest, null) ?? null;

  const siblings = parent
    ? parent.children.filter((c) => explorerNodeId(c) !== focusId)
    : forest.filter((c) => explorerNodeId(c) !== focusId);

  const pathTitles: string[] = [];
  const walkPath = (nodes: HierarchyNode<T>[], trail: string[]): boolean => {
    for (const n of nodes) {
      const next = [...trail, (n.mod.title || 'Part').trim() || 'Part'];
      if (explorerNodeId(n) === focusId) {
        pathTitles.push(...next);
        return true;
      }
      if (walkPath(n.children, next)) return true;
    }
    return false;
  };
  walkPath(forest, []);

  return {
    parent,
    children: node.children,
    siblings: siblings.slice(0, 8),
    pathTitles,
  };
}

export function systemAccent(category: string): string {
  return getCategoryColor(category);
}

export function assemblyStats<T extends HierarchyModLike & { image_url?: string | null; specifications?: unknown }>(
  node: HierarchyNode<T>
): { parts: number; assemblies: number; photos: number; specs: number } {
  let photos = 0;
  let specs = 0;
  const walk = (n: HierarchyNode<T>) => {
    if (n.mod.image_url) photos += 1;
    const sp = n.mod.specifications;
    if (sp && ((typeof sp === 'object' && Object.keys(sp as object).length > 0) || (typeof sp === 'string' && sp.trim()))) {
      specs += 1;
    }
    n.children.forEach(walk);
  };
  walk(node);
  return {
    parts: countChildren(node),
    assemblies: countAssemblies(node),
    photos,
    specs,
  };
}

export function isCarShowQueryParam(value: string | null): boolean {
  if (value === null) return false;
  const n = value.trim().toLowerCase();
  return n === '' || n === '1' || n === 'true' || n === 'yes' || n === 'carshow';
}
